import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';

const ReportButton = ({ itemType, itemId, variant = 'ghost', size = 'sm' }) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { key: 'spam', label: t('report.spam') },
    { key: 'inappropriate', label: t('report.inappropriate') },
    { key: 'fraudulent', label: t('report.fraudulent') },
    { key: 'discriminatory', label: t('report.discriminatory') },
    { key: 'duplicate', label: t('report.duplicate') },
    { key: 'other', label: t('report.other') }
  ];

  const handleSubmit = async () => {
    if (!reason) {
      toast.error(t('report.selectReason'));
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('report.loginToReport'));
        return;
      }

      await api.post(
        '/api/reports',
        {
          item_type: itemType,
          item_id: itemId,
          reason: reason,
          description: description
        },
        {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        }
      );

      toast.success(t('report.submitted'));
      setShowDialog(false);
      setReason('');
      setDescription('');
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error(t('report.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowDialog(true)}
        data-testid="report-button"
      >
        <Flag className="w-4 h-4 mr-2" />
        {t('report.report')}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>{t('report.reportTitle')} {itemType}</DialogTitle>
            <DialogDescription>
              {t('report.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">{t('report.reason')} *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="reason" data-testid="reason-select">
                  <SelectValue placeholder={t('report.selectReasonPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('report.additionalDetails')}</Label>
              <Textarea
                id="description"
                placeholder={t('report.detailsPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                data-testid="description-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={submitting}
            >
              {t('dashboard.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !reason}
              data-testid="submit-report-button"
            >
              {submitting ? t('report.submitting') : t('report.submitReport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportButton;
